import { ImageIcon, Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { buttonVariants, ResponsiveDialog } from 'ui';
import { cn } from 'utils/cn';
import { isInChromeExtension } from 'utils/proxyUtils';
import { getLightboxImageUrl, getLightboxOpen, setLightboxImageUrl, setLightboxOpen } from '../store/uiSlice';

export default function ImageLightbox() {
    const { t } = useTranslation();
    const [imageError, setImageError] = useState(false);
    const lightboxImageUrl = useSelector(getLightboxImageUrl);
    const lightboxOpen = useSelector(getLightboxOpen);
    const dispatch = useDispatch();

    const handleChange = (bool: boolean) => {
        if (!bool) dispatch(setLightboxImageUrl(undefined));
        dispatch(setLightboxOpen(bool));
    };

    const handleImageError = () => {
        setImageError(true);
    };

    return (
        <ResponsiveDialog open={lightboxOpen} onOpenChange={handleChange}>
            <ResponsiveDialog.Content
                aria-describedby={undefined}
                container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
                className="flex flex-col h-auto max-h-[90vh] w-full max-w-[90vw] items-center justify-center overflow-hidden bg-background z-50 min-h-0 gap-1 p-2"
            >
                <ResponsiveDialog.Title className="sr-only fixed">
                    Image preview
                </ResponsiveDialog.Title>
                {imageError ? (
                    <div className="flex flex-col items-center justify-center p-8 text-gray-400">
                        <ImageIcon size={42} />
                        <span className="mt-2 text-lg">The image is not available.</span>
                    </div>
                ) : (
                    <div
                        className="w-full min-h-0 max-h-[80vh] overflow-y-auto rounded-xl flex-1 overscroll-contain"
                        data-scrollable
                        onWheel={(e) => {
                            const el = e.currentTarget;
                            const atTop = el.scrollTop === 0;
                            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
                            // If we can scroll inside, prevent default page/overlay behavior
                            if (!(e.deltaY < 0 && atTop) && !(e.deltaY > 0 && atBottom)) {
                                e.preventDefault();
                            }
                            e.stopPropagation();
                            el.scrollTop += e.deltaY;
                        }}
                        style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                        <img
                            src={lightboxImageUrl}
                            onError={handleImageError}
                            className="max-w-full object-contain block mx-auto"
                        />
                    </div>
                )}
                {!isInChromeExtension() && (
                    <div className="shrink-0 mt-1 w-full flex justify-center">
                        <div className="flex items-center gap-2 justify-center text-textcolor">
                            <Info size={16} />
                            <p className="text-sm">
                                {t('lightbox.info', 'Not very accurate? Fix with our ')}
                                <a href='https://chromewebstore.google.com/detail/atarim-visual-website-col/ekpmbeilnaahlccpgjchjoonecikehf'
                                    target="_blank"
                                    className={cn(
                                        buttonVariants({
                                            variant: 'link',
                                            size: 'link',
                                        }),
                                        'text-sm font-normal'
                                    )}>{t('chrome_extension', 'Chrome Extension')}</a>
                            </p>
                        </div>
                    </div>
                )}
            </ResponsiveDialog.Content>
        </ResponsiveDialog>
    );
}